import React, { useState } from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { ConsultationForm } from '../components/ConsultationForm';
import { Shield, Lock, Eye, FileText, Users, Database, Bell, Globe, Mail } from 'lucide-react';

export const PrivacyPolicy = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);

  const openForm = () => {
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar onOpenForm={openForm} />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-[#0A1F44] to-[#153356]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center space-x-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full px-4 py-2 mb-6">
              <Shield className="w-5 h-5 text-[#D4AF37]" />
              <span className="text-[#D4AF37] text-sm font-medium">Privacy Policy</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Your Privacy Matters to Us
            </h1>
            <p className="text-xl text-gray-300">
              Last Updated: January 5, 2025
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Introduction */}
          <div className="mb-12">
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              At Otis Trademark, we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website, use our services, or communicate with us.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              By accessing or using our website and services, you acknowledge that you have read, understood, and agree to be bound by this Privacy Policy. If you do not agree with these terms, please discontinue use of our services immediately.
            </p>
          </div>

          {/* 1. Information We Collect */}
          <div className="mb-12">
            <div className="flex items-start space-x-3 mb-4">
              <Database className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
              <h2 className="text-2xl font-bold text-[#0A1F44]">1. Information We Collect</h2>
            </div>
            
            <h3 className="text-xl font-semibold text-[#0A1F44] mb-3 ml-9">1.1 Personal Information You Provide</h3>
            <p className="text-gray-700 leading-relaxed mb-4 ml-9">
              We collect personal information that you voluntarily provide to us when you:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-9 mb-6">
              <li>Create an account or register for our services</li>
              <li>Fill out contact forms, consultation requests, or application forms</li>
              <li>Subscribe to our newsletter or communications</li>
              <li>Communicate with us via email, phone, or chat</li>
              <li>Submit trademark applications or other legal documents</li>
              <li>Make payments for our services</li>
            </ul>

            <p className="text-gray-700 leading-relaxed mb-4 ml-9">
              The personal information we collect may include:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-9">
              <li>Full name and contact details (email address, phone number, mailing address)</li>
              <li>Business information (company name, business address, EIN)</li>
              <li>Account credentials (username, password)</li>
              <li>Payment information (credit card details, billing address)</li>
              <li>Trademark and intellectual property information</li>
              <li>Government-issued identification (when required for legal filings)</li>
              <li>Any other information you choose to provide</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#0A1F44] mb-3 mt-6 ml-9">1.2 Information Automatically Collected</h3>
            <p className="text-gray-700 leading-relaxed mb-4 ml-9">
              When you access our website, we automatically collect certain technical information, including:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-9">
              <li>IP address and geolocation data</li>
              <li>Browser type, version, and operating system</li>
              <li>Device information and unique identifiers</li>
              <li>Pages visited, time spent on pages, and navigation paths</li>
              <li>Referring/exit pages and clickstream data</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>
          </div>

          {/* 2. How We Use Your Information */}
          <div className="mb-12">
            <div className="flex items-start space-x-3 mb-4">
              <Eye className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
              <h2 className="text-2xl font-bold text-[#0A1F44]">2. How We Use Your Information</h2>
            </div>
            
            <p className="text-gray-700 leading-relaxed mb-4 ml-9">
              We use the information we collect for the following purposes:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-9">
              <li><strong>Service Provision:</strong> To provide, maintain, and improve our intellectual property legal services, including trademark and copyright registration, patent filing, and business formation</li>
              <li><strong>Account Management:</strong> To create and manage your account, verify your identity, and maintain the security of your account</li>
              <li><strong>Communication:</strong> To respond to your inquiries, provide customer support, send service-related notifications, and communicate about your legal matters</li>
              <li><strong>Legal Filings:</strong> To prepare and submit applications to the USPTO, Copyright Office, or other government agencies on your behalf</li>
              <li><strong>Payment Processing:</strong> To process payments, prevent fraud, and manage billing</li>
              <li><strong>Marketing:</strong> To send you promotional materials, newsletters, and information about our services (you may opt out at any time)</li>
              <li><strong>Analytics:</strong> To analyze website usage, improve our services, and enhance user experience</li>
              <li><strong>Legal Compliance:</strong> To comply with legal obligations, enforce our terms, and protect our rights and the rights of others</li>
            </ul>
          </div>

          {/* 3. License Grant and Usage Rights */}
          <div className="mb-12">
            <div className="flex items-start space-x-3 mb-4">
              <FileText className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
              <h2 className="text-2xl font-bold text-[#0A1F44]">3. License Grant and Usage Rights</h2>
            </div>
            
            <p className="text-gray-700 leading-relaxed mb-4 ml-9">
              By providing personal information and content to Otis Trademark, you grant us a worldwide, royalty-free, non-exclusive, and sublicensable license to use, reproduce, modify, adapt, publish, translate, and distribute your information and content solely for the purposes of:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-9 mb-4">
              <li>Enabling your use of our services</li>
              <li>Providing legal representation and filing services</li>
              <li>Communicating with government agencies and third parties on your behalf</li>
              <li>Maintaining records as required by legal and professional obligations</li>
            </ul>
            <p className="text-gray-700 leading-relaxed ml-9">
              <strong>Revocation:</strong> You may revoke this license at any time by requesting the deletion of your account and personal information. However, we may retain certain information as required by law or for legitimate business purposes (such as completed legal filings that must be maintained in our records).
            </p>
          </div>

          {/* 4. Information Sharing and Disclosure */}
          <div className="mb-12">
            <div className="flex items-start space-x-3 mb-4">
              <Users className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
              <h2 className="text-2xl font-bold text-[#0A1F44]">4. Information Sharing and Disclosure</h2>
            </div>
            
            <p className="text-gray-700 leading-relaxed mb-4 ml-9">
              We do not sell, rent, or trade your personal information. We may share your information with:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-9">
              <li><strong>Government Agencies:</strong> USPTO, Copyright Office, state agencies, and other governmental bodies as necessary for legal filings</li>
              <li><strong>Service Providers:</strong> Third-party vendors who perform services on our behalf (payment processors, email services, hosting providers, analytics providers)</li>
              <li><strong>Legal Counsel:</strong> Our attorneys and legal professionals who work on your matters</li>
              <li><strong>Legal Requirements:</strong> When required by law, court order, subpoena, or legal process</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets (with notice to you)</li>
              <li><strong>Protection of Rights:</strong> When necessary to protect our rights, property, safety, or the rights of others</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4 ml-9">
              All third-party service providers are contractually obligated to maintain the confidentiality and security of your information and may only use it for the purposes we specify.
            </p>
          </div>

          {/* 5. Data Security */}
          <div className="mb-12">
            <div className="flex items-start space-x-3 mb-4">
              <Lock className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
              <h2 className="text-2xl font-bold text-[#0A1F44]">5. Data Security</h2>
            </div>
            
            <p className="text-gray-700 leading-relaxed mb-4 ml-9">
              We implement industry-standard security measures to protect your personal information from unauthorized access, disclosure, alteration, and destruction. These measures include:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-9 mb-4">
              <li>Encryption of data in transit and at rest (SSL/TLS)</li>
              <li>Secure servers and data storage facilities</li>
              <li>Regular security audits and vulnerability assessments</li>
              <li>Access controls and authentication requirements</li>
              <li>Employee training on data protection practices</li>
              <li>Secure payment processing through PCI-compliant providers</li>
            </ul>
            <p className="text-gray-700 leading-relaxed ml-9 bg-yellow-50 border-l-4 border-yellow-400 p-4">
              <strong>Important:</strong> While we strive to protect your personal information, no method of transmission over the internet or electronic storage is 100% secure. You are responsible for maintaining the confidentiality of your account password and for any activities that occur under your account. Please notify us immediately if you believe your account has been compromised or accessed without authorization.
            </p>
          </div>

          {/* 6. Your Rights and Choices */}
          <div className="mb-12">
            <div className="flex items-start space-x-3 mb-4">
              <Shield className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
              <h2 className="text-2xl font-bold text-[#0A1F44]">6. Your Rights and Choices</h2>
            </div>
            
            <p className="text-gray-700 leading-relaxed mb-4 ml-9">You have the following rights regarding your personal information:</p>
            
            <ul className="list-disc list-inside space-y-3 text-gray-700 ml-9">
              <li><strong>Access:</strong> Request access to the personal information we hold about you</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal retention requirements)</li>
              <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications at any time</li>
              <li><strong>Data Portability:</strong> Request a copy of your data in a structured, machine-readable format</li>
              <li><strong>Restriction:</strong> Request restriction of processing in certain circumstances</li>
              <li><strong>Object:</strong> Object to processing of your personal information</li>
            </ul>

            <p className="text-gray-700 leading-relaxed mt-4 ml-9">
              To exercise any of these rights, please contact us at <a href="mailto:privacy@otistrademark.com" className="text-[#D4AF37] hover:underline">privacy@otistrademark.com</a> or use our contact form. We will respond to your request within 30 days.
            </p>
          </div>

          {/* 7. Cookies and Tracking Technologies */}
          <div className="mb-12">
            <div className="flex items-start space-x-3 mb-4">
              <Globe className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
              <h2 className="text-2xl font-bold text-[#0A1F44]">7. Cookies and Tracking Technologies</h2>
            </div>
            
            <p className="text-gray-700 leading-relaxed mb-4 ml-9">
              We use cookies and similar tracking technologies to enhance your experience on our website. Cookies are small text files stored on your device that help us:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-9 mb-4">
              <li>Remember your preferences and settings</li>
              <li>Maintain your login session</li>
              <li>Analyze website traffic and usage patterns</li>
              <li>Deliver personalized content and advertisements</li>
              <li>Improve website functionality and performance</li>
            </ul>
            <p className="text-gray-700 leading-relaxed ml-9">
              You can control cookie preferences through your browser settings. However, disabling cookies may limit your ability to use certain features of our website.
            </p>
          </div>

          {/* 8. Third-Party Links */}
          <div className="mb-12">
            <div className="flex items-start space-x-3 mb-4">
              <Globe className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
              <h2 className="text-2xl font-bold text-[#0A1F44]">8. Third-Party Websites and Links</h2>
            </div>
            
            <p className="text-gray-700 leading-relaxed ml-9">
              Our website may contain links to third-party websites, including the USPTO, Copyright Office, and other external resources. This Privacy Policy does not apply to these third-party sites. We are not responsible for the privacy practices or content of external websites. We encourage you to review the privacy policies of any third-party sites you visit.
            </p>
          </div>

          {/* 9. Children's Privacy */}
          <div className="mb-12">
            <div className="flex items-start space-x-3 mb-4">
              <Shield className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
              <h2 className="text-2xl font-bold text-[#0A1F44]">9. Children's Privacy</h2>
            </div>
            
            <p className="text-gray-700 leading-relaxed ml-9">
              Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately, and we will take steps to delete such information.
            </p>
          </div>

          {/* 10. Data Retention */}
          <div className="mb-12">
            <div className="flex items-start space-x-3 mb-4">
              <Database className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
              <h2 className="text-2xl font-bold text-[#0A1F44]">10. Data Retention</h2>
            </div>
            
            <p className="text-gray-700 leading-relaxed mb-4 ml-9">
              We retain your personal information for as long as necessary to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-9">
              <li>Provide our services to you</li>
              <li>Comply with legal and professional obligations (including attorney-client privilege and record-keeping requirements)</li>
              <li>Resolve disputes and enforce our agreements</li>
              <li>Maintain business records</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4 ml-9">
              Legal files and correspondence are retained in accordance with bar association rules and legal professional standards, which may require indefinite retention of certain documents.
            </p>
          </div>

          {/* 11. International Users */}
          <div className="mb-12">
            <div className="flex items-start space-x-3 mb-4">
              <Globe className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
              <h2 className="text-2xl font-bold text-[#0A1F44]">11. International Data Transfers</h2>
            </div>
            
            <p className="text-gray-700 leading-relaxed ml-9">
              Otis Trademark is based in the United States. If you access our services from outside the United States, please be aware that your information may be transferred to, stored, and processed in the United States where our servers are located and our central database is operated. By using our services, you consent to the transfer of your information to the United States and other countries that may have different data protection laws than your country of residence.
            </p>
          </div>

          {/* 12. Changes to Privacy Policy */}
          <div className="mb-12">
            <div className="flex items-start space-x-3 mb-4">
              <Bell className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
              <h2 className="text-2xl font-bold text-[#0A1F44]">12. Changes to This Privacy Policy</h2>
            </div>
            
            <p className="text-gray-700 leading-relaxed ml-9">
              We may update this Privacy Policy from time to time to reflect changes in our practices, legal requirements, or business operations. We will notify you of any material changes by:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-9 mt-4 mb-4">
              <li>Posting the updated Privacy Policy on our website with a new "Last Updated" date</li>
              <li>Sending an email notification to your registered email address</li>
              <li>Displaying a prominent notice on our website</li>
            </ul>
            <p className="text-gray-700 leading-relaxed ml-9">
              Your continued use of our services after the effective date of the updated Privacy Policy constitutes acceptance of the changes.
            </p>
          </div>

          {/* 13. Disclaimer and Assumption of Risk */}
          <div className="mb-12">
            <div className="flex items-start space-x-3 mb-4">
              <FileText className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
              <h2 className="text-2xl font-bold text-[#0A1F44]">13. Disclaimer and Assumption of Risk</h2>
            </div>
            
            <p className="text-gray-700 leading-relaxed ml-9 bg-red-50 border-l-4 border-red-400 p-4">
              <strong>Important Notice:</strong> You assume total responsibility and risk for your use of our website and the services provided through it. While we implement security measures to protect your information, you acknowledge that internet transmissions are never completely secure. You are responsible for maintaining the security of your devices, implementing appropriate security software, and exercising caution when transmitting sensitive information online.
            </p>
          </div>

          {/* Contact Information */}
          <div className="bg-gradient-to-br from-[#0A1F44] to-[#153356] rounded-2xl p-8 text-white">
            <h2 className="text-2xl font-bold mb-4">Contact Us About Privacy</h2>
            <p className="text-gray-300 mb-6">
              If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
            </p>
            
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <Mail className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-1" />
                <div>
                  <div className="font-semibold">Email:</div>
                  <a href="mailto:privacy@otistrademark.com" className="text-[#D4AF37] hover:underline">
                    privacy@otistrademark.com
                  </a>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <FileText className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-1" />
                <div>
                  <div className="font-semibold">Mailing Address:</div>
                  <div className="text-gray-300">
                    Otis Trademark<br />
                    Privacy Department<br />
                    1140 Harrison St<br />
                    San Francisco, CA 94103
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      <Footer />
      <ConsultationForm isOpen={isFormOpen} onClose={closeForm} formType="privacy" />
    </div>
  );
};
